import { useState, type ComponentType, type CSSProperties } from 'react';
import { getCompanyLogoDefaultSrc } from '../company/get-company-logo-url';
import { getCompanyLogoInitials } from '../company/company-logo-fallback-content';
// Round 7: per-icon subpath imports (not the '@phosphor-icons/react' barrel), so the
// static search panel island only bundles the icons it actually uses instead of the
// whole ~1300-icon package (the barrel's index.es.js bundles every icon's path data as
// one non-tree-shakeable object, confirmed by its own file size vs. one icon file's).
import { Headset } from '@phosphor-icons/react/dist/csr/Headset';
import { Handshake } from '@phosphor-icons/react/dist/csr/Handshake';
import { AddressBook } from '@phosphor-icons/react/dist/csr/AddressBook';
import { Translate } from '@phosphor-icons/react/dist/csr/Translate';
import { FileSql } from '@phosphor-icons/react/dist/csr/FileSql';
import { BracketsCurly } from '@phosphor-icons/react/dist/csr/BracketsCurly';
import { Lightning } from '@phosphor-icons/react/dist/csr/Lightning';
import { Fire } from '@phosphor-icons/react/dist/csr/Fire';
import { Hammer } from '@phosphor-icons/react/dist/csr/Hammer';
import { HardHat } from '@phosphor-icons/react/dist/csr/HardHat';
import { Broom } from '@phosphor-icons/react/dist/csr/Broom';
import { CookingPot } from '@phosphor-icons/react/dist/csr/CookingPot';
import { Pipe } from '@phosphor-icons/react/dist/csr/Pipe';
import { Wrench } from '@phosphor-icons/react/dist/csr/Wrench';
import { Car } from '@phosphor-icons/react/dist/csr/Car';
import { Package } from '@phosphor-icons/react/dist/csr/Package';
import { Calculator } from '@phosphor-icons/react/dist/csr/Calculator';
import { Money } from '@phosphor-icons/react/dist/csr/Money';
import { ShieldCheck } from '@phosphor-icons/react/dist/csr/ShieldCheck';
import { Ruler } from '@phosphor-icons/react/dist/csr/Ruler';
import { UsersThree } from '@phosphor-icons/react/dist/csr/UsersThree';
import { Globe } from '@phosphor-icons/react/dist/csr/Globe';
import { House } from '@phosphor-icons/react/dist/csr/House';
import { Buildings } from '@phosphor-icons/react/dist/csr/Buildings';
import { MapPin } from '@phosphor-icons/react/dist/csr/MapPin';
import { Briefcase } from '@phosphor-icons/react/dist/csr/Briefcase';
import { ChartBar } from '@phosphor-icons/react/dist/csr/ChartBar';
import { GraduationCap } from '@phosphor-icons/react/dist/csr/GraduationCap';
import { Code } from '@phosphor-icons/react/dist/csr/Code';
import { Cpu } from '@phosphor-icons/react/dist/csr/Cpu';
import { ChartLineUp } from '@phosphor-icons/react/dist/csr/ChartLineUp';
import { HandCoins } from '@phosphor-icons/react/dist/csr/HandCoins';
import { Heartbeat } from '@phosphor-icons/react/dist/csr/Heartbeat';
import { MicrosoftExcelLogo } from '@phosphor-icons/react/dist/csr/MicrosoftExcelLogo';
import { MicrosoftPowerpointLogo } from '@phosphor-icons/react/dist/csr/MicrosoftPowerpointLogo';
import { MicrosoftWordLogo } from '@phosphor-icons/react/dist/csr/MicrosoftWordLogo';
import { MicrosoftOutlookLogo } from '@phosphor-icons/react/dist/csr/MicrosoftOutlookLogo';
import { MicrosoftTeamsLogo } from '@phosphor-icons/react/dist/csr/MicrosoftTeamsLogo';
import { WindowsLogo } from '@phosphor-icons/react/dist/csr/WindowsLogo';
import { AmazonLogo } from '@phosphor-icons/react/dist/csr/AmazonLogo';
import { LinkedinLogo } from '@phosphor-icons/react/dist/csr/LinkedinLogo';
import { SlackLogo } from '@phosphor-icons/react/dist/csr/SlackLogo';
import {
  siReact,
  siTypescript,
  siPython,
  siDocker,
  siKubernetes,
  siPostgresql,
  siGraphql,
  siJavascript,
  siLinux,
  siMongodb,
  siRedis,
  siTerraform,
  siJenkins,
  siFigma,
  siSwift,
  siKotlin,
  siRuby,
  siPhp,
  siRust,
  siScala,
  siGo,
  siGooglecloud,
  siGnubash,
  siApachekafka,
  siApachespark,
  siAutodeskrevit,
  siCplusplus,
  siNodedotjs,
  siAngular,
  siVuedotjs,
  siNextdotjs,
  siJira,
  siConfluence,
  siHubspot,
  siSap,
  siApachecassandra,
  siDjango,
  siFlask,
  siSpring,
  siExpress,
  siFastapi,
  siNumpy,
  siPandas,
  siTensorflow,
  siPytorch,
  siJupyter,
  siFirebase,
  siVercel,
  siNetlify,
  siNginx,
  siApache,
  siWordpress,
  siShopify,
  siStripe,
  siAnthropic,
  siMysql,
  siElasticsearch,
  siDatabricks,
  type SimpleIcon,
} from 'simple-icons/icons';
// Per-country flag SVG URLs (flag-icons, MIT), not its global stylesheet: see country-flag-urls.ts.
import { getCountryFlagUrl } from './country-flag-urls';
import { getSkillIcon } from '../skills/skill-icons';
// Imported from its own small module (step 5, static search panel island), not from
// components/filters/FilterBuildingBlocks.tsx: that file also pulls in redux hooks,
// LocationHierarchyPicker, FinlandGlobeFlagThumb and 20+ unrelated phosphor icons for its other
// exports, none of which this lookup needs. See utils/filters/benefit-icons.tsx's own comment.
import { getBenefitIcon } from '../filters/benefit-icons';

/**
 * Search panel spec (search-panel-spec.md) step 3: shared icon lookup for a `(attribute, value)`
 * facet pair, used by every SearchPanel row/chip/tile so a value always looks the same everywhere
 * it appears in the panel. Resolution order per attribute:
 * - `companyId`: the real company logo (CompanyLogo).
 * - `skillsAndTechnologies`: a simple-icons brand mark (curated alias table below) in its own
 *   colour tile, then a Phosphor brand-logo glyph for the handful of brands simple-icons doesn't
 *   carry (Office apps, AWS, LinkedIn, Slack), then a curated non-tech Phosphor icon (customer
 *   service, trades, etc.), then the existing `getSkillIcon` category fallback
 *   (web-app/utils/skills/skill-icons.tsx, fixed in step 1).
 * - `benefits`: `getBenefitIcon` (extended below with a few more common values).
 * - `remoteness`: globe / house / office building.
 * - `location`: a pin for a Finnish city, a globe for a "Remote - ..." pseudo-location, a
 *   flag-icons flag for a recognised foreign country, else a pin.
 * - `requiredLanguages`: a plain two-letter code badge, deliberately never a flag (a language
 *   isn't a country, and flags for languages are a well-known bad pattern e.g. English != any one
 *   flag).
 * - `field` / `companyIndustry` / `jobType` / `normalisedRoleLevel` / `educationLevel`: a curated
 *   icon per common value, else a sensible per-attribute default.
 * - anything else: no icon (null).
 */

export type FacetIconRender =
  | { kind: 'company'; companyId: string }
  | { kind: 'brand'; icon: SimpleIcon; tileBg: string; glyphColor: string }
  | { kind: 'phosphor'; Icon: PhosphorIconComponent; tileBg?: string; glyphColor?: string }
  | { kind: 'monogram'; text: string; tileBg: string; textColor: string }
  | { kind: 'flag'; countryCode: string; url: string }
  | { kind: 'language'; code: string }
  | { kind: 'none' };

type PhosphorIconComponent = ComponentType<{ className?: string; weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone'; style?: CSSProperties }>;

function normalize(value: string): string {
  return value.toLowerCase().trim();
}

/** simple-icons brand marks for common `skillsAndTechnologies` values. Keys are normalized
 * (lowercase) aliases; several aliases can point at the same icon (e.g. go/golang). Brands
 * simple-icons doesn't carry (Microsoft Office apps, AWS, LinkedIn, Slack) are handled by
 * PHOSPHOR_BRAND_ICONS below instead, per the spec's explicit list. */
const SIMPLE_ICON_ALIASES: Record<string, SimpleIcon> = {
  react: siReact,
  'react native': siReact,
  typescript: siTypescript,
  python: siPython,
  docker: siDocker,
  kubernetes: siKubernetes,
  k8s: siKubernetes,
  postgresql: siPostgresql,
  postgres: siPostgresql,
  graphql: siGraphql,
  javascript: siJavascript,
  linux: siLinux,
  mongodb: siMongodb,
  redis: siRedis,
  terraform: siTerraform,
  jenkins: siJenkins,
  figma: siFigma,
  swift: siSwift,
  kotlin: siKotlin,
  ruby: siRuby,
  php: siPhp,
  rust: siRust,
  scala: siScala,
  go: siGo,
  golang: siGo,
  gcp: siGooglecloud,
  'google cloud': siGooglecloud,
  'google cloud platform': siGooglecloud,
  bash: siGnubash,
  'shell scripting': siGnubash,
  kafka: siApachekafka,
  'apache kafka': siApachekafka,
  spark: siApachespark,
  'apache spark': siApachespark,
  revit: siAutodeskrevit,
  'c++': siCplusplus,
  'node.js': siNodedotjs,
  nodejs: siNodedotjs,
  node: siNodedotjs,
  angular: siAngular,
  vue: siVuedotjs,
  'vue.js': siVuedotjs,
  'next.js': siNextdotjs,
  nextjs: siNextdotjs,
  jira: siJira,
  confluence: siConfluence,
  hubspot: siHubspot,
  sap: siSap,
  // Not "java: siOpenjdk": coordinator review round 6 deliberately routes Java to the monogram
  // tile below (MONOGRAM_BRAND_ICONS) instead, real Java brand orange (#E76F00), not OpenJDK's
  // duke mark on a black tile.
  cassandra: siApachecassandra,
  django: siDjango,
  flask: siFlask,
  spring: siSpring,
  'spring boot': siSpring,
  express: siExpress,
  'express.js': siExpress,
  fastapi: siFastapi,
  numpy: siNumpy,
  pandas: siPandas,
  tensorflow: siTensorflow,
  pytorch: siPytorch,
  jupyter: siJupyter,
  firebase: siFirebase,
  vercel: siVercel,
  netlify: siNetlify,
  nginx: siNginx,
  apache: siApache,
  wordpress: siWordpress,
  shopify: siShopify,
  stripe: siStripe,
  anthropic: siAnthropic,
  claude: siAnthropic,
  mysql: siMysql,
  elasticsearch: siElasticsearch,
  databricks: siDatabricks,
};

/** React's cyan (#61DAFB) reads as a highlight colour, not a fillable tile background (the
 * project's own approved design boards render it on a fixed dark tile, `#20232a`, not on cyan) -
 * every other brand tile uses the icon's own hex as the tile background with a white glyph. */
const DARK_TILE_BRAND_KEYS = new Set(['react', 'react native']);
const REACT_DARK_TILE = '#20232a';

/** Brands simple-icons doesn't carry, or that this product renders as a Phosphor logo glyph
 * instead per the spec (Office apps -> their own logo icons except the umbrella "MS Office" ->
 * WindowsLogo, AWS -> AmazonLogo, LinkedIn, Slack, Teams). Coordinator review round 6: these used
 * to render on the same pale generic accent tile every non-brand skill gets, since `kind:
 * 'phosphor'`'s `tileBg`/`glyphColor` were left unset; now each carries the real brand colour as
 * the tile background with a white glyph (matching how a simple-icons brand tile already
 * renders), same list as SIMPLE_ICON_ALIASES's own comment describes. AWS is the one exception:
 * its tile is AWS's own dark navy, with the glyph in AWS's orange, not white-on-brand. */
const PHOSPHOR_BRAND_ICONS: Record<string, { Icon: PhosphorIconComponent; tileBg: string; glyphColor: string }> = {
  excel: { Icon: MicrosoftExcelLogo, tileBg: '#217346', glyphColor: '#ffffff' },
  'ms excel': { Icon: MicrosoftExcelLogo, tileBg: '#217346', glyphColor: '#ffffff' },
  'microsoft excel': { Icon: MicrosoftExcelLogo, tileBg: '#217346', glyphColor: '#ffffff' },
  powerpoint: { Icon: MicrosoftPowerpointLogo, tileBg: '#B7472A', glyphColor: '#ffffff' },
  'ms powerpoint': { Icon: MicrosoftPowerpointLogo, tileBg: '#B7472A', glyphColor: '#ffffff' },
  'microsoft powerpoint': { Icon: MicrosoftPowerpointLogo, tileBg: '#B7472A', glyphColor: '#ffffff' },
  word: { Icon: MicrosoftWordLogo, tileBg: '#2B579A', glyphColor: '#ffffff' },
  'ms word': { Icon: MicrosoftWordLogo, tileBg: '#2B579A', glyphColor: '#ffffff' },
  'microsoft word': { Icon: MicrosoftWordLogo, tileBg: '#2B579A', glyphColor: '#ffffff' },
  outlook: { Icon: MicrosoftOutlookLogo, tileBg: '#0078D4', glyphColor: '#ffffff' },
  teams: { Icon: MicrosoftTeamsLogo, tileBg: '#5059C9', glyphColor: '#ffffff' },
  'ms teams': { Icon: MicrosoftTeamsLogo, tileBg: '#5059C9', glyphColor: '#ffffff' },
  'microsoft teams': { Icon: MicrosoftTeamsLogo, tileBg: '#5059C9', glyphColor: '#ffffff' },
  'ms office': { Icon: WindowsLogo, tileBg: '#D83B01', glyphColor: '#ffffff' },
  'microsoft office': { Icon: WindowsLogo, tileBg: '#D83B01', glyphColor: '#ffffff' },
  'microsoft 365': { Icon: WindowsLogo, tileBg: '#D83B01', glyphColor: '#ffffff' },
  office365: { Icon: WindowsLogo, tileBg: '#D83B01', glyphColor: '#ffffff' },
  microsoft: { Icon: WindowsLogo, tileBg: '#D83B01', glyphColor: '#ffffff' },
  aws: { Icon: AmazonLogo, tileBg: '#232F3E', glyphColor: '#FF9900' },
  'amazon web services': { Icon: AmazonLogo, tileBg: '#232F3E', glyphColor: '#FF9900' },
  linkedin: { Icon: LinkedinLogo, tileBg: '#0A66C2', glyphColor: '#ffffff' },
  slack: { Icon: SlackLogo, tileBg: '#4A154B', glyphColor: '#ffffff' },
};

/** Brands with no glyph anywhere (no simple-icons entry, no Phosphor logo either): a plain
 * brand-colour tile with a bold 2-4 letter monogram instead. Power BI's tile is light (its own
 * brand yellow), so its text stays dark to actually read; every other monogram tile is white on
 * the brand colour, same as PHOSPHOR_BRAND_ICONS above. */
const MONOGRAM_BRAND_ICONS: Record<string, { text: string; tileBg: string; textColor: string }> = {
  azure: { text: 'Az', tileBg: '#0078D4', textColor: '#ffffff' },
  'microsoft azure': { text: 'Az', tileBg: '#0078D4', textColor: '#ffffff' },
  salesforce: { text: 'SF', tileBg: '#00A1E0', textColor: '#ffffff' },
  java: { text: 'Java', tileBg: '#E76F00', textColor: '#ffffff' },
  'c#': { text: 'C#', tileBg: '#512BD4', textColor: '#ffffff' },
  csharp: { text: 'C#', tileBg: '#512BD4', textColor: '#ffffff' },
  tableau: { text: 'Tb', tileBg: '#E97627', textColor: '#ffffff' },
  'power bi': { text: 'BI', tileBg: '#F2C811', textColor: '#1E1A14' },
  powerbi: { text: 'BI', tileBg: '#F2C811', textColor: '#1E1A14' },
  oracle: { text: 'Or', tileBg: '#C74634', textColor: '#ffffff' },
};

/** Curated non-tech Phosphor icons for the common values in skills200.txt that aren't brands
 * (customer service, trades, soft skills, etc.). Not exhaustive: the long tail still falls
 * through to `getSkillIcon`'s category-keyword fallback (step 1). */
const PHOSPHOR_SKILL_ICONS: Record<string, PhosphorIconComponent> = {
  'customer service': Headset,
  sales: Handshake,
  'b2b sales': Handshake,
  prospecting: Handshake,
  negotiation: Handshake,
  crm: AddressBook,
  'crm systems': AddressBook,
  english: Translate,
  finnish: Translate,
  swedish: Translate,
  'language skills': Translate,
  sql: FileSql,
  'sql server': FileSql,
  api: BracketsCurly,
  apis: BracketsCurly,
  rest: BracketsCurly,
  'rest apis': BracketsCurly,
  json: BracketsCurly,
  electrical: Lightning,
  'electrical systems': Lightning,
  'electrical design': Lightning,
  welding: Fire,
  carpentry: Hammer,
  construction: HardHat,
  'construction & trades': HardHat,
  cleaning: Broom,
  'food preparation': CookingPot,
  cooking: CookingPot,
  plumbing: Pipe,
  maintenance: Wrench,
  'equipment maintenance': Wrench,
  driving: Car,
  'driving license': Car,
  logistics: Package,
  inventory: Package,
  'inventory management': Package,
  procurement: Package,
  budgeting: Calculator,
  forecasting: Calculator,
  'cash handling': Money,
  'cash register': Money,
  safety: ShieldCheck,
  'health & safety': ShieldCheck,
  'workplace safety': ShieldCheck,
  cad: Ruler,
  bim: Ruler,
  'technical drawings': Ruler,
  'team leadership': UsersThree,
  teamwork: UsersThree,
  'stakeholder management': UsersThree,
};

/** Locations known to be Finnish cities (the launchpad's own top-7 list and the SRP's Finnish
 * baseline draw from the same pool): a small allowlist here is enough to pick "pin" over "globe"
 * without needing a full gazetteer lookup for an icon choice. Anything not on this list, not a
 * remote pseudo-location, and not a recognised foreign country still falls back to a plain pin. */
const FINNISH_CITY_NAMES = new Set([
  'helsinki', 'espoo', 'tampere', 'vantaa', 'oulu', 'turku', 'jyvaskyla', 'jyväskylä', 'lahti',
  'kuopio', 'pori', 'kouvola', 'joensuu', 'lappeenranta', 'hameenlinna', 'hämeenlinna', 'vaasa',
  'seinajoki', 'seinäjoki', 'rovaniemi', 'mikkeli', 'kotka', 'salo', 'porvoo', 'kokkola',
]);

/** Country name -> ISO 3166-1 alpha-2, in English/Finnish/Swedish (the three collections' own
 * `location` values), for the countries most likely to appear given the seeded gazetteer's
 * current coverage. Not exhaustive; an unrecognised foreign country falls back to a plain pin
 * rather than guessing wrong. */
const COUNTRY_NAME_TO_ISO: Record<string, string> = {
  finland: 'fi', suomi: 'fi',
  sweden: 'se', ruotsi: 'se', sverige: 'se',
  norway: 'no', norja: 'no', norge: 'no',
  denmark: 'dk', tanska: 'dk', danmark: 'dk',
  estonia: 'ee', viro: 'ee', estland: 'ee',
  germany: 'de', saksa: 'de', tyskland: 'de',
  'united kingdom': 'gb', 'iso-britannia': 'gb', storbritannien: 'gb', uk: 'gb',
  ireland: 'ie', irlanti: 'ie', irland: 'ie',
  netherlands: 'nl', alankomaat: 'nl', nederlanderna: 'nl',
  france: 'fr', ranska: 'fr', frankrike: 'fr',
  spain: 'es', espanja: 'es', spanien: 'es',
  italy: 'it', italia: 'it',
  poland: 'pl', puola: 'pl', polen: 'pl',
  lithuania: 'lt', liettua: 'lt', litauen: 'lt',
  latvia: 'lv', latvia_sv: 'lv',
  portugal: 'pt', portugali: 'pt',
  switzerland: 'ch', sveitsi: 'ch', schweiz: 'ch',
  austria: 'at', itavalta: 'at', österrike: 'at',
  belgium: 'be', belgia: 'be', belgien: 'be',
  'united states': 'us', yhdysvallat: 'us', usa: 'us',
  canada: 'ca', kanada: 'ca',
  india: 'in', intia: 'in',
  'united arab emirates': 'ae', 'arabiemiraatit': 'ae',
  romania: 'ro', romania_fi: 'ro',
  ukraine: 'ua', ukraina: 'ua',
};

/** Language name -> two-letter code badge, en/fi/sv. */
const LANGUAGE_NAME_TO_CODE: Record<string, string> = {
  english: 'EN', englanti: 'EN', engelska: 'EN',
  finnish: 'FI', suomi: 'FI', finska: 'FI',
  swedish: 'SV', ruotsi: 'SV', svenska: 'SV',
  german: 'DE', saksa: 'DE', tyska: 'DE',
  russian: 'RU', venaja: 'RU', ryska: 'RU',
  estonian: 'ET', viro: 'ET', estniska: 'ET',
  french: 'FR', ranska: 'FR', franska: 'FR',
  spanish: 'ES', espanja: 'ES', spanska: 'ES',
};

const FIELD_ICONS: Record<string, PhosphorIconComponent> = {
  // Coordinator review round 1, bug 8: Technology and Software engineering used to share `Code`,
  // reading as duplicate tiles in the launchpad grid; Technology gets its own glyph.
  technology: Cpu,
  'software engineering': Code,
  it: Code,
  sales: Handshake,
  'construction & trades': HardHat,
  construction: HardHat,
  'operations & supply chain': Package,
  logistics: Package,
  healthcare: Heartbeat,
  finance: HandCoins,
  marketing: ChartLineUp,
};

const ATTRIBUTE_DEFAULT_ICON: Record<string, PhosphorIconComponent> = {
  field: Briefcase,
  // Roles (normalisedTitle) had no entry, so role rows rendered an empty lead slot.
  normalisedTitle: Briefcase,
  companyIndustry: Buildings,
  jobType: Briefcase,
  normalisedRoleLevel: ChartBar,
  educationLevel: GraduationCap,
};

export function tokenizeRemoteness(value: string): 'remote' | 'hybrid' | 'onsite' {
  const lower = normalize(value);
  if (lower.includes('remote') || lower.includes('etä') || lower.includes('distans')) return 'remote';
  if (lower.includes('hybrid')) return 'hybrid';
  return 'onsite';
}

/** Resolves the country ISO code for a `location` facet value like "Berlin, Germany" or
 * "Helsinki, Finland": takes the last comma-separated segment (the country) and looks it up.
 * Returns null for a bare city name or an unrecognised country. */
function resolveLocationCountryCode(value: string): string | null {
  const segments = value.split(',').map((s) => normalize(s));
  const countrySegment = segments[segments.length - 1];
  if (!countrySegment) return null;
  return COUNTRY_NAME_TO_ISO[countrySegment] ?? null;
}

function isRemotePseudoLocation(value: string): boolean {
  const lower = normalize(value);
  return lower.startsWith('remote') || lower.startsWith('etätyö') || lower.startsWith('etatyo') || lower.startsWith('distans');
}

/**
 * Resolves `(attribute, value)` to how it should render. Never throws; returns `{ kind: 'none' }`
 * for anything with no sensible icon rather than guessing.
 */
export function resolveFacetIcon(attribute: string, value: string): FacetIconRender {
  if (!value) return { kind: 'none' };
  const lower = normalize(value);

  if (attribute === 'companyId') {
    return { kind: 'company', companyId: value };
  }

  if (attribute === 'skillsAndTechnologies') {
    const brand = SIMPLE_ICON_ALIASES[lower];
    if (brand) {
      const darkTile = DARK_TILE_BRAND_KEYS.has(lower);
      return {
        kind: 'brand',
        icon: brand,
        tileBg: darkTile ? REACT_DARK_TILE : `#${brand.hex}`,
        glyphColor: darkTile ? `#${brand.hex}` : '#ffffff',
      };
    }
    const phosphorBrand = PHOSPHOR_BRAND_ICONS[lower];
    if (phosphorBrand) return { kind: 'phosphor', Icon: phosphorBrand.Icon, tileBg: phosphorBrand.tileBg, glyphColor: phosphorBrand.glyphColor };
    const monogramBrand = MONOGRAM_BRAND_ICONS[lower];
    if (monogramBrand) return { kind: 'monogram', text: monogramBrand.text, tileBg: monogramBrand.tileBg, textColor: monogramBrand.textColor };
    const curated = PHOSPHOR_SKILL_ICONS[lower];
    if (curated) return { kind: 'phosphor', Icon: curated };
    const categoryIcon = getSkillIcon(value);
    if (categoryIcon) return { kind: 'phosphor', Icon: categoryIcon };
    return { kind: 'none' };
  }

  if (attribute === 'benefits') {
    return { kind: 'phosphor', Icon: getBenefitIcon(value) };
  }

  if (attribute === 'remoteness') {
    const setup = tokenizeRemoteness(value);
    if (setup === 'remote') return { kind: 'phosphor', Icon: Globe };
    if (setup === 'hybrid') return { kind: 'phosphor', Icon: House };
    return { kind: 'phosphor', Icon: Buildings };
  }

  if (attribute === 'location') {
    if (isRemotePseudoLocation(value)) return { kind: 'phosphor', Icon: Globe };
    const bareCity = normalize(value.split(',')[0] ?? '');
    if (FINNISH_CITY_NAMES.has(bareCity)) return { kind: 'phosphor', Icon: MapPin };
    const countryCode = resolveLocationCountryCode(value);
    const flagUrl = countryCode ? getCountryFlagUrl(countryCode) : null;
    if (countryCode && flagUrl) return { kind: 'flag', countryCode, url: flagUrl };
    return { kind: 'phosphor', Icon: MapPin };
  }

  if (attribute === 'requiredLanguages') {
    const code = LANGUAGE_NAME_TO_CODE[lower] ?? value.slice(0, 2).toUpperCase();
    return { kind: 'language', code };
  }

  if (attribute === 'field') {
    const curated = FIELD_ICONS[lower];
    return { kind: 'phosphor', Icon: curated ?? ATTRIBUTE_DEFAULT_ICON.field };
  }

  const defaultIcon = ATTRIBUTE_DEFAULT_ICON[attribute];
  if (defaultIcon) return { kind: 'phosphor', Icon: defaultIcon };

  return { kind: 'none' };
}

export { FINNISH_CITY_NAMES, COUNTRY_NAME_TO_ISO, LANGUAGE_NAME_TO_CODE };

/**
 * Bundle-size fix (step 5, static search panel island): a light stand-in for
 * components/CompanyLogo.tsx, used ONLY here. That component's own chain pulls in the full
 * compiled i18n catalogue (utils/company/format-company-logo-alt-label-sync.ts, reached via
 * utils/company/index.ts) even though FacetIcon only ever needs a decorative company mark, not a
 * captioned/linked logo. This renders the same srcset helper (utils/company/get-company-logo-url.ts,
 * confirmed catalogue-free) with a plain initials tile fallback on image error, decorative
 * `alt=""` since the facet row/chip next to it already carries the real accessible label.
 */
/**
 * Round 14 (coordinator finding, from a headless screenshot): top-match rows showed blank space
 * where the logo should be. Two separate causes, both fixed here:
 * 1. This used to be a single-attempt `<img>` (the constructed `/logos/{kebab-key}-128.webp` URL,
 *    nothing else) with no second fallback source. `components/CompanyLogo.tsx` (the full version
 *    this is a light stand-in for, see its own doc comment) always tries a SECOND source
 *    (`hostedLogoUrl`, a stored/validated URL a search hit itself can carry) before giving up to
 *    initials; this now does the same `constructed -> hostedLogoUrl -> initials` chain, just
 *    without CompanyLogo's srcset/picture machinery (this file's whole reason to exist is staying
 *    catalogue/dependency-light for the static island's bundle, see this file's own top comment).
 * 2. TopHitsList (below) used to render this ONLY when `hit.companyId` was present, and some
 *    search hits carry `companyLabel` (the display name) but no `companyId` (an opaque per-record
 *    key some data sources never populate): those hits got no logo slot AT ALL, real blank space,
 *    not even an initials tile. TopHitsList now falls back to `companyLabel` as the key whenever
 *    `companyId` is missing, so a tile always renders as long as there's SOME company name.
 */
function PanelCompanyLogo({ companyKey, hostedLogoUrl, size, className }: { companyKey: string; hostedLogoUrl?: string; size: number; className?: string }) {
  // 0: constructed URL (from companyKey), 1: hostedLogoUrl (if any), 2: give up, show initials.
  const [attempt, setAttempt] = useState(0);
  const style: CSSProperties = { width: size, height: size, borderRadius: size * 0.3, flexShrink: 0 };

  const src = attempt === 0 ? getCompanyLogoDefaultSrc(companyKey) : hostedLogoUrl;
  const exhausted = attempt >= 2 || (attempt === 1 && !hostedLogoUrl);

  if (exhausted || !src) {
    return (
      <span
        className={className}
        style={{ ...style, background: 'var(--jc-accent-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.4, fontWeight: 700, color: 'var(--jc-accent)' }}
        aria-hidden="true"
      >
        {getCompanyLogoInitials(companyKey)}
      </span>
    );
  }

  return (
    <img
      key={src}
      src={src}
      alt=""
      className={className}
      style={{ ...style, objectFit: 'contain' }}
      loading="lazy"
      onError={() => setAttempt((a) => a + 1)}
    />
  );
}

/** Round 14: TopHitsList's own entry point (exported, unlike PanelCompanyLogo itself), since it's
 * the only caller with hit-level `hostedLogoUrl`/`companyLabel` fallback data; every other
 * `FacetIcon attribute="companyId"` caller (facet rows, chips) has neither and keeps using the
 * plain `resolveFacetIcon` path below unchanged. */
export function TopHitCompanyLogo({ companyId, companyLabel, hostedLogoUrl, size, className }: { companyId?: string; companyLabel?: string; hostedLogoUrl?: string; size: number; className?: string }) {
  const companyKey = (companyId || companyLabel || '').trim();
  if (!companyKey) return null;
  return <PanelCompanyLogo companyKey={companyKey} hostedLogoUrl={hostedLogoUrl} size={size} className={className} />;
}

export interface FacetIconProps {
  attribute: string;
  value: string;
  /** Tile size in px; the glyph inside is sized proportionally. Matches the boards' 24-34px range. */
  size?: number;
  className?: string;
}

/**
 * Renders whatever `resolveFacetIcon` resolves to as one square/round tile, so every SearchPanel
 * row, chip and "add with it" pill can just do `<FacetIcon attribute={...} value={...} />`
 * without caring which of the five render kinds it got back.
 */
export function FacetIcon({ attribute, value, size = 24, className }: FacetIconProps) {
  const render = resolveFacetIcon(attribute, value);
  const style: CSSProperties = { width: size, height: size };

  if (render.kind === 'none') return null;

  if (render.kind === 'company') {
    return <PanelCompanyLogo companyKey={render.companyId} size={size} className={className} />;
  }

  if (render.kind === 'brand') {
    return (
      <span
        className={className}
        style={{ ...style, borderRadius: size * 0.3, background: render.tileBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
      >
        <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 24 24" aria-hidden="true">
          <path fill={render.glyphColor} d={render.icon.path} />
        </svg>
      </span>
    );
  }

  if (render.kind === 'flag') {
    return (
      <img
        src={render.url}
        alt=""
        aria-hidden="true"
        width={size}
        height={size * 0.75}
        loading="lazy"
        decoding="async"
        className={className}
        style={{ width: size, height: size * 0.75, borderRadius: 4, flexShrink: 0, objectFit: 'cover' }}
      />
    );
  }

  if (render.kind === 'language') {
    return (
      <span
        className={className}
        style={{ ...style, borderRadius: size * 0.3, background: 'var(--jc-elevated)', color: 'var(--jc-on-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.4, fontWeight: 700, flexShrink: 0 }}
      >
        {render.code}
      </span>
    );
  }

  if (render.kind === 'monogram') {
    return (
      <span
        className={className}
        style={{
          ...style,
          borderRadius: size * 0.3,
          background: render.tileBg,
          color: render.textColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: render.text.length > 2 ? size * 0.32 : size * 0.42,
          flexShrink: 0,
        }}
      >
        {render.text}
      </span>
    );
  }

  // 'phosphor': an outline glyph in a light accent circle, matching every other action-side glyph
  // in the approved boards (the tile itself is left to the caller when it wants a plain icon with
  // no circle, e.g. inside an already-tiled row; `tileBg`/`glyphColor` let a caller override). A
  // real brand tile (PHOSPHOR_BRAND_ICONS, e.g. Excel/AWS/Slack) also sets `tileBg` but wants the
  // same rounded-square shape simple-icons brand tiles use, not the generic accent circle.
  const Icon = render.Icon;
  const isBrandTile = render.tileBg !== undefined;
  return (
    <span
      className={className}
      style={{
        ...style,
        borderRadius: isBrandTile ? size * 0.3 : 999,
        background: render.tileBg ?? 'var(--jc-accent-muted)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icon style={{ width: size * (isBrandTile ? 0.6 : 0.56), height: size * (isBrandTile ? 0.6 : 0.56), color: render.glyphColor ?? 'var(--jc-accent)' }} />
    </span>
  );
}
